/** EIP-6963 multi-wallet discovery, with a window.ethereum fallback for wallets that predate it. */
export interface Eip1193Provider {
  request(args: { method: string; params?: unknown }): Promise<unknown>;
  on?(event: string, listener: (...args: never[]) => void): void;
  removeListener?(event: string, listener: (...args: never[]) => void): void;
}
export interface WalletOption {
  uuid: string;
  name: string;
  icon: string | null;
  rdns: string;
  provider: Eip1193Provider;
  /** 'injected' = browser extension (or the dev signer); 'embedded' = a wallet held for a signed-in user (Privy). */
  kind: 'injected' | 'embedded';
  /** One line shown under the name in the wallet picker. */
  hint?: string;
  /**
   * Shown under the address in the account menu instead of the name, read with useSyncExternalStore: the embedded
   * wallet's name is a picker action ("Log in with email"), so it names the signed-in account there instead.
   */
  account?: { get(): string | null; subscribe(listener: () => void): () => void };
  /** Ends the session behind the wallet (embedded wallets); the app forgets the wallet either way. */
  disconnect?(): Promise<void>;
}

/** rdns of the Privy-backed option, see privy.tsx. */
export const EMBEDDED_RDNS = 'io.privy.embedded';

const found = new Map<string, WalletOption>();
const listeners = new Set<() => void>();
let snapshot: WalletOption[] = [];

function publish(): void {
  snapshot = [...found.values()].sort((a, b) => a.name.localeCompare(b.name));
  listeners.forEach((l) => l());
}

export function announce(option: WalletOption): void {
  if ([...found.values()].some((w) => w.provider === option.provider)) return;
  found.set(option.rdns || option.uuid, option);
  publish();
}

window.addEventListener('eip6963:announceProvider', ((e: CustomEvent<{ info: { uuid: string; name: string; icon: string; rdns: string }; provider: Eip1193Provider }>) => {
  const d = e.detail;
  if (!d?.provider || !d.info) return;
  // Only data: icons are rendered; anything else is dropped (the icon comes from an extension, treat it as untrusted).
  announce({ uuid: d.info.uuid, name: d.info.name, rdns: d.info.rdns, icon: /^data:image\//.test(d.info.icon) ? d.info.icon : null, provider: d.provider, kind: 'injected' });
}) as EventListener);

export function requestProviders(): void {
  window.dispatchEvent(new Event('eip6963:requestProvider'));
  // Legacy injection: give 6963 wallets a moment to answer first so the same wallet is not listed twice.
  setTimeout(() => {
    const legacy = (window as { ethereum?: Eip1193Provider }).ethereum;
    if (legacy && !snapshot.some((w) => w.kind === 'injected')) announce({ uuid: 'legacy', name: 'Browser wallet', icon: null, rdns: 'window.ethereum', provider: legacy, kind: 'injected' });
  }, 400);
}

export const subscribeWallets = (l: () => void): (() => void) => { listeners.add(l); return () => listeners.delete(l); };
export const getWallets = (): WalletOption[] => snapshot;
